"use client";

import { Capacitor } from "@capacitor/core";
import {
  DevicePermissionError,
  isPermissionDeniedError,
} from "@/lib/native/permission-guidance";

export type CurrentLocationOptions = {
  enableHighAccuracy?: boolean;
  maximumAge?: number;
  timeout?: number;
};

export type CurrentLocationPosition = {
  coords: {
    latitude: number;
    longitude: number;
    accuracy?: number | null;
    altitude?: number | null;
    altitudeAccuracy?: number | null;
    heading?: number | null;
    speed?: number | null;
  };
  timestamp: number;
};

function getBrowserCurrentPosition(
  options: CurrentLocationOptions,
): Promise<CurrentLocationPosition> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    throw new DevicePermissionError(
      "location",
      "Geolocation is not available in this browser.",
      "unsupported",
    );
  }

  return new Promise<CurrentLocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

async function getNativeCurrentPosition(
  options: CurrentLocationOptions,
): Promise<CurrentLocationPosition> {
  const { Geolocation } = await import("@capacitor/geolocation");

  try {
    let permissionStatus = await Geolocation.checkPermissions();
    const hasLocationPermission =
      permissionStatus.location === "granted" ||
      permissionStatus.coarseLocation === "granted";

    if (!hasLocationPermission) {
      permissionStatus = await Geolocation.requestPermissions({
        permissions: ["location"],
      });
    }

    if (
      permissionStatus.location !== "granted" &&
      permissionStatus.coarseLocation !== "granted"
    ) {
      throw new DevicePermissionError(
        "location",
        "Location access is turned off in system settings.",
      );
    }

    return Geolocation.getCurrentPosition({
      enableHighAccuracy: options.enableHighAccuracy,
      maximumAge: options.maximumAge,
      timeout: options.timeout,
    });
  } catch (error) {
    if (error instanceof DevicePermissionError) throw error;

    if (isPermissionDeniedError(error)) {
      throw new DevicePermissionError(
        "location",
        error instanceof Error ? error.message : "Location permission denied.",
      );
    }

    throw error;
  }
}

/**
 * Best-effort position for centring a map when a screen opens. Never asks for
 * permission on native, and never throws: `onPosition` simply doesn't run when
 * there is no fix (`onNoPosition` does, for callers that need to know).
 *
 * Screens must use this, never `navigator.geolocation`, for any read nobody
 * tapped for. In the native WebView the browser API is WebKit's, not the OS's:
 * on iOS it raises a SECOND "app.hiive.club would like to use your location"
 * dialog on top of the system one, and on Android Capacitor's chrome client
 * turns it into a real runtime permission request, so opening the Explore map
 * asked for location unprompted (and its 30s poll kept asking after a denial).
 * Taps that should ask go through `getCurrentDevicePosition` instead.
 *
 * On the web this is the plain browser call, which prompts as before.
 */
export function peekDevicePosition(
  onPosition: (position: CurrentLocationPosition) => void,
  options: CurrentLocationOptions = {},
  onNoPosition?: () => void,
): void {
  const read = async (): Promise<CurrentLocationPosition | null> => {
    if (!Capacitor.isNativePlatform()) {
      return getBrowserCurrentPosition(options);
    }
    const { Geolocation } = await import("@capacitor/geolocation");
    const status = await Geolocation.checkPermissions();
    if (status.location !== "granted" && status.coarseLocation !== "granted") {
      return null;
    }
    return Geolocation.getCurrentPosition({
      enableHighAccuracy: options.enableHighAccuracy,
      maximumAge: options.maximumAge,
      timeout: options.timeout,
    });
  };

  read().then(
    (position) => {
      if (position) onPosition(position);
      else onNoPosition?.();
    },
    () => {
      // Services off, denied on the web, or timeout: keep the caller's fallback.
      onNoPosition?.();
    },
  );
}

export function getCurrentDevicePosition(
  options: CurrentLocationOptions = {},
): Promise<CurrentLocationPosition> {
  if (Capacitor.isNativePlatform()) {
    return getNativeCurrentPosition(options);
  }

  return getBrowserCurrentPosition(options);
}
